import { z } from 'zod';
import { costSummaryFigureSchema, type CostSummaryFigure } from './costing';
import { metricKeySchema } from './dashboard';
import { entityReference } from './entity-references';
import { environmentSchema, infrastructureStatusSchema } from './infrastructure';
import { mapLayerKeySchema } from './mapping';
import type { Permission } from './permissions';
import { REGISTER_KINDS, registerKindSchema } from './register';
import { WORKFLOW_STATUSES, workflowStatusSchema } from './scheduling';

/**
 * What a cost tile shows before anybody configures it: the four figures that
 * answer "are we going to overspend". Named rather than inlined because the
 * catalog entry's `defaultConfiguration` and the schema's own `.default()` must
 * be the same list, and two literals drift.
 */
const COST_TILE_DEFAULT_FIGURES: CostSummaryFigure[] = [
  'currentBudget',
  'realized',
  'anticipatedFinalCost',
  'variance',
];

/**
 * The component catalog (architecture documentation sections 8.3 and 9).
 *
 * A component type is registered as data, not as code: identifier, permissions,
 * configuration schema, data binding, sizing and refresh policy. The API uses
 * this registry to validate saved views; the frontend uses the same registry to
 * render the picker and the configuration form. One definition, two consumers,
 * no drift.
 *
 * Adding a component means adding an entry here plus a renderer keyed by the
 * same identifier -- no changes to the view schema, the persistence layer, or
 * existing components (goal: new modules with minimal coupling, section 3).
 */

export const COMPONENT_CATEGORIES = ['monitoring', 'planning', 'content'] as const;
export const componentCategorySchema = z.enum(COMPONENT_CATEGORIES);

/**
 * How a component keeps itself current. `interval` components are polled by the
 * frontend query layer; `manual` components refetch only on user action or
 * navigation. Streaming is reserved for data that genuinely changes faster than
 * a poll can follow (section 15.3) and is unused by the initial catalog.
 */
export const refreshPolicySchema = z.discriminatedUnion('policy', [
  z.object({ policy: z.literal('manual') }),
  z.object({ policy: z.literal('interval'), intervalSeconds: z.number().int().min(15).max(3600) }),
  z.object({ policy: z.literal('stream'), channel: z.string() }),
]);
export type RefreshPolicy = z.infer<typeof refreshPolicySchema>;

export const gridSizeSchema = z.object({
  width: z.number().int().min(1).max(12),
  height: z.number().int().min(1).max(12),
});
export type GridSize = z.infer<typeof gridSizeSchema>;

export interface ComponentDefinition<TConfig extends z.ZodTypeAny = z.ZodTypeAny> {
  type: string;
  titleKey: string;
  descriptionKey: string;
  category: (typeof COMPONENT_CATEGORIES)[number];
  /** Enforced on save and again on read; a later permission change must not leak data. */
  requiredPermissions: readonly Permission[];
  configSchema: TConfig;
  defaultConfiguration: unknown;
  defaultSize: GridSize;
  minSize: GridSize;
  refresh: RefreshPolicy;
  /** Logical read model this component binds to, resolved by the frontend data layer. */
  dataSource: string | null;
  /** Bumped when the configuration shape changes, so saved views can be migrated. */
  configVersion: number;
}

const kpiMetricConfig = z
  .object({
    /* `metricKeySchema` rather than a second enum listing the same keys: the
       tile configures a metric the metrics endpoint has to serve, so one list
       that drifts into two is a tile the server answers 400 for. */
    metric: metricKeySchema,
    environment: environmentSchema.optional(),
    showTrend: z.boolean().default(true),
  })
  .strict();

const serviceStatusConfig = z
  .object({
    environment: environmentSchema.optional(),
    statuses: z.array(infrastructureStatusSchema).min(1).optional(),
    limit: z.number().int().min(1).max(50).default(10),
  })
  .strict();

const serviceLatencyConfig = z
  .object({
    /** Picked by name in the editor; see `entityReference`. */
    serviceIds: z.array(entityReference('infrastructure_asset')).min(1).max(5),
    window: z.enum(['1h', '6h', '24h', '7d', '30d']).default('24h'),
  })
  .strict();

/**
 * Both project components below bind to the workspace's project rather than
 * naming one (architecture documentation section 2). That is why neither
 * carries a project identifier in its configuration: a deployment is about one
 * project, so a field asking which one could only ever hold the answer the
 * shell already knows -- and a wrong answer would be a dashboard quietly
 * reporting on something else.
 */
const projectSummaryConfig = z
  .object({
    /**
     * Rows the tile shows, in the order given. Health and progress are the two
     * a reader checks first, so they are the default; dates are opt-in because
     * a project with no dates set would otherwise show two empty rows.
     */
    fields: z
      .array(z.enum(['status', 'health', 'progress', 'startsOn', 'endsOn']))
      .min(1)
      .max(5)
      .default(['status', 'health', 'progress']),
  })
  .strict();

const scheduleOverviewConfig = z.object({}).strict();

/**
 * The two schedule tiles: the chart and the board, on a dashboard.
 *
 * The schedule page answers "what is the plan"; a dashboard answers "what
 * should I look at today", and until now the only schedule presence it had was
 * four counters. These place the actual views next to the cost curve and the
 * map, and they are read-only by construction -- no edit mode, no dragging, no
 * change session (`ScheduleGanttTileRenderer`'s own comment says why).
 *
 * Both share one idea about configuration: a tile is glanced at in a small box,
 * so the question a configuration field answers is "which slice of the schedule
 * is this tile about", never "where does the data come from" -- the project
 * answers that, here as everywhere else.
 */
const scheduleTileFilterFields = {
  /**
   * Only the viewer's own work: tasks they own, contribute to or watch.
   *
   * Evaluated per reader rather than stored as a user id, so one placed tile
   * shows each person their own work rather than showing everyone the work of
   * whoever configured it.
   */
  mineOnly: z.boolean().default(false),
  /** Empty means every status, which is the right default for an overview. */
  statuses: z.array(workflowStatusSchema).max(WORKFLOW_STATUSES.length).default([]),
  /** Only tasks carrying a warning or that could not be scheduled at all. */
  attentionOnly: z.boolean().default(false),
};

const scheduleGanttConfig = z
  .object({
    ...scheduleTileFilterFields,
    /**
     * How far either side of today the tile draws. A dashboard Gantt showing a
     * four-year programme end to end is a picture of a project, not a thing to
     * read: tasks that finish before the window or start after it are left out,
     * and the chart fits itself to what remains.
     */
    pastDays: z.number().int().min(0).max(3650).default(30),
    futureDays: z.number().int().min(1).max(3650).default(120),
    /**
     * How many levels of the work breakdown structure to draw, counting the top
     * level as 1. Three is a plan; ten is a spreadsheet in a box.
     */
    maxDepth: z.number().int().min(1).max(10).default(3),
    /** Dependency connectors between bars; off by default in a small box. */
    showDependencies: z.boolean().default(false),
  })
  .strict();

const scheduleBoardConfig = z
  .object({
    ...scheduleTileFilterFields,
    /**
     * Cards per column. The board page shows every card; a tile that does the
     * same turns into a scrolling column of forty, so it takes the first few
     * and says how many more there are.
     */
    cardsPerColumn: z.number().int().min(1).max(50).default(5),
  })
  .strict();

/**
 * The map tile.
 *
 * Configuration is about what a *reader* should see first, not about where the
 * data comes from -- the project answers that, as everywhere else. Two of the
 * three fields exist because a map on a dashboard is a different thing from a
 * map on the map page: it is glanced at, in a small box, next to five other
 * tiles. So it opens on the layers this tile is about, with the controls that
 * would crowd a small box switched off by default, and the full-page map is one
 * click away for anyone who wants to work in it.
 */
/**
 * A ceiling where the layer enum used to be one.
 *
 * `.max(MAP_FEATURE_LAYERS.length)` meant "all of them" for as long as layers
 * were a closed set. They are a catalog now, so the bound has to be a number
 * chosen for what a tile can usefully draw rather than for how many layers
 * happen to exist -- a tile naming forty of them is a tile with no filter.
 */
const MAX_TILE_LAYERS = 12;

const projectMapConfig = z
  .object({
    /**
     * Layers drawn when the tile loads. Empty means every layer, which is the
     * right default for an overview tile and the wrong one for a tile somebody
     * placed specifically to watch possessions.
     */
    layers: z.array(mapLayerKeySchema).max(MAX_TILE_LAYERS).default([]),
    /**
     * Off by default: a search box inside a four-column tile is a control the
     * reader will mis-click on the way to something else, and the full-page map
     * has a better one.
     */
    showSearch: z.boolean().default(false),
    showLegend: z.boolean().default(true),
    /**
     * Null means "fit to the data", which is almost always what a tile wants:
     * it stays correct when the project's footprint changes. A stored zoom is
     * for a tile deliberately pinned to one site.
     */
    zoom: z.number().min(0).max(18).nullable().default(null),
  })
  .strict();

/**
 * The two planning tiles the meetings and register modules bring with them.
 *
 * Both bind to the workspace's project rather than naming one, for the reason
 * given above the project components: a deployment is about one project, and a
 * field asking which one could only hold the answer the shell already has.
 */
const upcomingMeetingsConfig = z
  .object({
    limit: z.number().int().min(1).max(10).default(3),
    /**
     * Whether to list the open actions each meeting produced beneath it.
     *
     * On by default, because "what did we agree, and has it happened" is the
     * question a meetings tile is on a dashboard to answer; a list of dates is
     * a calendar, and the reader already has one.
     */
    showOpenActions: z.boolean().default(true),
  })
  .strict();

const registerSummaryConfig = z
  .object({
    /** Which kinds to count. Empty is not offered: a tile that counts nothing
     *  is a tile with a heading. */
    kinds: z.array(registerKindSchema).min(1).max(REGISTER_KINDS.length).default(['risk', 'issue']),
  })
  .strict();

const markdownNoteConfig = z
  .object({
    /**
     * Stored as plain text and rendered through a restrictive sanitizer on the
     * client. Never inserted as raw HTML (section 14.2).
     */
    body: z.string().max(8000).default(''),
  })
  .strict();

/**
 * The two cost tiles.
 *
 * Costing was the largest module in the product with no dashboard presence at
 * all, which made the money the one thing a reader had to navigate to find. Two
 * tiles rather than one, because "what are the numbers" and "where is this
 * heading" are different questions with different shapes: a figure list answers
 * the first at a glance in a small box, and only a curve answers the second.
 *
 * Neither names a project, for the reason the project components give: a
 * deployment is about one project and the shell already knows which.
 */
const costSummaryConfig = z
  .object({
    /**
     * The amounts to show, in the order given.
     *
     * The default is the four-figure answer to "are we going to overspend":
     * what was agreed, what has been spent, where it lands, and the difference.
     * Six is the ceiling because a tile is read at a glance -- past that it is a
     * report, and the Costs page is a better one.
     */
    figures: z.array(costSummaryFigureSchema).min(1).max(6).default(COST_TILE_DEFAULT_FIGURES),
    /**
     * The share of the current budget already realized, as a meter.
     *
     * On by default and separate from `figures` because it is the one number
     * here that is not money: a percentage belongs on its own scale, and
     * putting it in the figure list would invite a reader to compare it with
     * the euros beside it.
     */
    showBudgetUsed: z.boolean().default(true),
  })
  .strict();

const costCurveConfig = z
  .object({
    /**
     * Months of history to draw, counting back from the current period, or null
     * for the whole project.
     *
     * Null is the default because the S-curve is a whole-project artefact --
     * the budget it is read against has no date of its own -- and a window
     * chosen once would be wrong for every project but the one it was chosen
     * for. The option exists for a long project whose early periods have
     * stopped being interesting; the cumulative values stay true when it is
     * used, because the API accumulates over the whole series and windows
     * afterwards.
     */
    trailingMonths: z.number().int().min(3).max(60).nullable().default(null),
    /**
     * The forecast continuation of the realized line. On by default: a cost
     * curve cut off at today answers where the project has been and not where
     * it is going, which is the question the tile is on a dashboard for.
     */
    showForecast: z.boolean().default(true),
  })
  .strict();

/**
 * The two operational tiles: the providers, and the rules.
 *
 * Both are counts with a worst-first list under them, and both exist because
 * the module that owns them shipped a navigation tab and nothing a reader would
 * see without clicking it. Each is deliberately read-only -- a write button on
 * a shared dashboard is an accidental change waiting for a mis-click, and the
 * page one click away has the controls.
 *
 * A third tile reported the depth of the background job queue. It moved to the
 * provider console with the Operations page: the queue belongs to the
 * deployment, and a tile a client administrator can read but not act on is a
 * worry rather than a report.
 */
/**
 * The document register's tile: what is still waiting to be issued.
 *
 * A count of documents is furniture -- a project has as many as it has. What a
 * reader acts on is the drawing somebody downstream is waiting for, which is
 * exactly the row with no issued revision.
 */
const documentRegisterConfig = z
  .object({
    /** Narrow to one discipline, for a tile a track engineer keeps open. */
    discipline: z.string().max(120).nullish(),
    limit: z.number().int().min(1).max(10).default(5),
  })
  .strict();

/*
 * Fleet tiles (contracts: fleet.ts, fleet-compliance.ts).
 *
 * The period is configuration rather than "the current one", because the two
 * questions a reader has are different: "are we on track" is about the period
 * running now, and "how did we finish" is about the one that closed. A tile
 * that silently switched over on 1 January 2026 would answer the second
 * question with the first one's tile.
 */
const fleetComplianceConfig = z
  .object({
    period: z.string().max(32).optional(),
    /** Narrow to one category group, for a deployment whose fleet is all
     *  buses and whose reader does not need three rows to say so. */
    categoryGroup: z.enum(['light_duty', 'trucks', 'buses']).optional(),
  })
  .strict();

const fleetDataQualityConfig = z.object({}).strict();

const fleetEnergyMixConfig = z
  .object({
    /** Draw the alternative-fuel split as a second band. Off by default: the
     *  mix itself is the answer most readers want, and the split is a second
     *  question they ask once. */
    showAlternativeSplit: z.boolean(),
  })
  .strict();

const fleetVerificationConfig = z.object({}).strict();

const fleetAgreementExpiryConfig = z
  .object({ withinDays: z.number().int().min(7).max(1095) })
  .strict();

const integrationHealthConfig = z
  .object({
    /**
     * Show only the providers in trouble, rather than every configured one.
     *
     * On by default, and it is what makes this tile small enough to sit beside
     * five others: a healthy deployment shows one line saying so, and a
     * deployment with a broken provider shows the broken provider.
     */
    onlyUnhealthy: z.boolean().default(true),
    limit: z.number().int().min(1).max(10).default(5),
  })
  .strict();

const automationActivityConfig = z
  .object({
    limit: z.number().int().min(1).max(10).default(5),
    /**
     * Whether runs that evaluated their conditions and did nothing count as
     * activity worth listing.
     *
     * On by default, because the automations module records skipped runs
     * precisely so that "it did not fire" and "it never ran" stay
     * distinguishable, and a tile that hid them would collapse the distinction
     * the engine went to the trouble of keeping.
     */
    includeSkipped: z.boolean().default(true),
  })
  .strict();

const discussionActivityConfig = z
  .object({
    limit: z.number().int().min(1).max(10).default(5),
  })
  .strict();

export const COMPONENT_CATALOG = {
  'kpi-metric': {
    type: 'kpi-metric',
    titleKey: 'components.kpiMetric.title',
    descriptionKey: 'components.kpiMetric.description',
    category: 'monitoring',
    requiredPermissions: ['infrastructure.read'],
    configSchema: kpiMetricConfig,
    defaultConfiguration: { metric: 'services.degraded', showTrend: true },
    defaultSize: { width: 3, height: 2 },
    minSize: { width: 2, height: 2 },
    refresh: { policy: 'interval', intervalSeconds: 60 },
    dataSource: 'dashboard.metric',
    configVersion: 1,
  },
  'service-status': {
    type: 'service-status',
    titleKey: 'components.serviceStatus.title',
    descriptionKey: 'components.serviceStatus.description',
    category: 'monitoring',
    requiredPermissions: ['infrastructure.read'],
    configSchema: serviceStatusConfig,
    defaultConfiguration: { limit: 10 },
    defaultSize: { width: 6, height: 4 },
    minSize: { width: 3, height: 3 },
    refresh: { policy: 'interval', intervalSeconds: 30 },
    dataSource: 'infrastructure.services',
    configVersion: 1,
  },
  'service-latency': {
    type: 'service-latency',
    titleKey: 'components.serviceLatency.title',
    descriptionKey: 'components.serviceLatency.description',
    category: 'monitoring',
    requiredPermissions: ['infrastructure.read'],
    configSchema: serviceLatencyConfig,
    defaultConfiguration: { serviceIds: [], window: '24h' },
    defaultSize: { width: 6, height: 4 },
    minSize: { width: 4, height: 3 },
    refresh: { policy: 'interval', intervalSeconds: 60 },
    dataSource: 'infrastructure.timeSeries',
    configVersion: 1,
  },
  'project-summary': {
    type: 'project-summary',
    titleKey: 'components.projectSummary.title',
    descriptionKey: 'components.projectSummary.description',
    category: 'planning',
    requiredPermissions: ['project.read'],
    configSchema: projectSummaryConfig,
    defaultConfiguration: { fields: ['status', 'health', 'progress'] },
    defaultSize: { width: 4, height: 3 },
    minSize: { width: 3, height: 2 },
    refresh: { policy: 'interval', intervalSeconds: 300 },
    dataSource: 'projects.get',
    configVersion: 1,
  },
  'schedule-overview': {
    type: 'schedule-overview',
    titleKey: 'components.scheduleOverview.title',
    descriptionKey: 'components.scheduleOverview.description',
    category: 'planning',
    requiredPermissions: ['schedule.read'],
    configSchema: scheduleOverviewConfig,
    defaultConfiguration: {},
    defaultSize: { width: 4, height: 3 },
    minSize: { width: 3, height: 2 },
    refresh: { policy: 'interval', intervalSeconds: 120 },
    dataSource: 'scheduling.tasks',
    configVersion: 2,
  },
  'schedule-gantt': {
    type: 'schedule-gantt',
    titleKey: 'components.scheduleGantt.title',
    descriptionKey: 'components.scheduleGantt.description',
    category: 'planning',
    requiredPermissions: ['schedule.read'],
    configSchema: scheduleGanttConfig,
    defaultConfiguration: {
      mineOnly: false,
      statuses: [],
      attentionOnly: false,
      pastDays: 30,
      futureDays: 120,
      maxDepth: 3,
      showDependencies: false,
    },
    /**
     * The same argument as the map's, for the same reason: below a size a Gantt
     * stops being readable rather than becoming a smaller Gantt. The floor is
     * where a name column, a few bars and the header band still fit at once.
     */
    defaultSize: { width: 8, height: 6 },
    minSize: { width: 5, height: 4 },
    refresh: { policy: 'interval', intervalSeconds: 300 },
    dataSource: 'scheduling.tasks',
    configVersion: 1,
  },
  'schedule-board': {
    type: 'schedule-board',
    titleKey: 'components.scheduleBoard.title',
    descriptionKey: 'components.scheduleBoard.description',
    category: 'planning',
    requiredPermissions: ['schedule.read'],
    configSchema: scheduleBoardConfig,
    defaultConfiguration: {
      mineOnly: false,
      statuses: [],
      attentionOnly: false,
      cardsPerColumn: 5,
    },
    defaultSize: { width: 6, height: 5 },
    minSize: { width: 4, height: 3 },
    refresh: { policy: 'interval', intervalSeconds: 300 },
    dataSource: 'scheduling.tasks',
    configVersion: 1,
  },
  'project-map': {
    type: 'project-map',
    titleKey: 'components.projectMap.title',
    descriptionKey: 'components.projectMap.description',
    category: 'planning',
    requiredPermissions: ['map.read'],
    configSchema: projectMapConfig,
    defaultConfiguration: { layers: [], showSearch: false, showLegend: true, zoom: null },
    /**
     * Larger than any other tile, and with a tall floor. A map is the one
     * component whose usefulness collapses below a size: three grid cells of
     * coastline is decoration. The minimum is the point at which the legend, a
     * hover card and enough surrounding context still fit at once.
     */
    defaultSize: { width: 8, height: 6 },
    minSize: { width: 4, height: 4 },
    /**
     * Slower than the status tiles beside it, deliberately. A map re-render is
     * expensive and visually disruptive -- the camera holds, but every layer is
     * re-evaluated -- and the things it draws move on the timescale of a work
     * shift, not of a health check.
     */
    refresh: { policy: 'interval', intervalSeconds: 120 },
    dataSource: 'mapping.features',
    configVersion: 1,
  },
  'upcoming-meetings': {
    type: 'upcoming-meetings',
    titleKey: 'components.upcomingMeetings.title',
    descriptionKey: 'components.upcomingMeetings.description',
    category: 'planning',
    requiredPermissions: ['meeting.read'],
    configSchema: upcomingMeetingsConfig,
    defaultConfiguration: { limit: 3, showOpenActions: true },
    defaultSize: { width: 4, height: 4 },
    minSize: { width: 3, height: 3 },
    /* Meetings move on the timescale of a working week. Polling one is a
     * request that will find nothing changed roughly three hundred times in a
     * row, so this refreshes on navigation like the note does. */
    refresh: { policy: 'manual' },
    dataSource: 'meetings.upcoming',
    configVersion: 1,
  },
  'register-summary': {
    type: 'register-summary',
    titleKey: 'components.registerSummary.title',
    descriptionKey: 'components.registerSummary.description',
    category: 'planning',
    requiredPermissions: ['register.read'],
    configSchema: registerSummaryConfig,
    defaultConfiguration: { kinds: ['risk', 'issue'] },
    defaultSize: { width: 3, height: 3 },
    minSize: { width: 2, height: 2 },
    refresh: { policy: 'interval', intervalSeconds: 300 },
    dataSource: 'register.summary',
    configVersion: 1,
  },
  'document-register': {
    type: 'document-register',
    titleKey: 'components.documentRegister.title',
    descriptionKey: 'components.documentRegister.description',
    category: 'planning',
    requiredPermissions: ['document.read'],
    configSchema: documentRegisterConfig,
    defaultConfiguration: { discipline: null, limit: 5 },
    defaultSize: { width: 3, height: 3 },
    minSize: { width: 2, height: 2 },
    refresh: { policy: 'interval', intervalSeconds: 300 },
    dataSource: 'document.register',
    configVersion: 1,
  },
  'cost-summary': {
    type: 'cost-summary',
    titleKey: 'components.costSummary.title',
    descriptionKey: 'components.costSummary.description',
    category: 'planning',
    /**
     * `cost.read` alone, deliberately. `cost.read_detail` governs individual
     * entries -- who was paid what -- and every figure here is a project-level
     * total that the coarse permission already covers. Requiring the finer one
     * would hide the budget from the people whose job is to watch it.
     */
    requiredPermissions: ['cost.read'],
    configSchema: costSummaryConfig,
    defaultConfiguration: { figures: COST_TILE_DEFAULT_FIGURES, showBudgetUsed: true },
    defaultSize: { width: 4, height: 4 },
    minSize: { width: 3, height: 3 },
    /* Money moves when somebody posts an entry or a sync lands, not
     * continuously. Five minutes matches the register tile beside it. */
    refresh: { policy: 'interval', intervalSeconds: 300 },
    dataSource: 'costing.summary',
    configVersion: 1,
  },
  'cost-curve': {
    type: 'cost-curve',
    titleKey: 'components.costCurve.title',
    descriptionKey: 'components.costCurve.description',
    category: 'planning',
    requiredPermissions: ['cost.read'],
    configSchema: costCurveConfig,
    defaultConfiguration: { trailingMonths: null, showForecast: true },
    /**
     * Wide and tall, on the map tile's reasoning: four cumulative curves and a
     * budget threshold in a three-cell box is a smear. The floor is where the
     * legend, the "now" rule and enough periods to read a slope still fit.
     */
    defaultSize: { width: 8, height: 5 },
    minSize: { width: 4, height: 4 },
    refresh: { policy: 'interval', intervalSeconds: 300 },
    dataSource: 'costing.timeseries',
    configVersion: 1,
  },
  'fleet-compliance': {
    type: 'fleet-compliance',
    titleKey: 'components.fleetCompliance.title',
    descriptionKey: 'components.fleetCompliance.description',
    category: 'planning',
    requiredPermissions: ['fleet.report.read'],
    configSchema: fleetComplianceConfig,
    defaultConfiguration: {},
    defaultSize: { width: 6, height: 3 },
    minSize: { width: 3, height: 2 },
    /* Five minutes, like the register summary. The underlying rows change when
       somebody declares a vehicle, which is a weekly event at most -- a shorter
       interval would poll a report nobody has changed. */
    refresh: { policy: 'interval', intervalSeconds: 300 },
    dataSource: 'fleet.compliance',
    configVersion: 1,
  },
  'fleet-data-quality': {
    type: 'fleet-data-quality',
    titleKey: 'components.fleetDataQuality.title',
    descriptionKey: 'components.fleetDataQuality.description',
    category: 'planning',
    requiredPermissions: ['fleet.read'],
    configSchema: fleetDataQualityConfig,
    defaultConfiguration: {},
    defaultSize: { width: 3, height: 3 },
    minSize: { width: 2, height: 2 },
    refresh: { policy: 'interval', intervalSeconds: 300 },
    dataSource: 'fleet.dataQuality',
    configVersion: 1,
  },
  'fleet-agreement-expiry': {
    type: 'fleet-agreement-expiry',
    titleKey: 'components.fleetAgreementExpiry.title',
    descriptionKey: 'components.fleetAgreementExpiry.description',
    category: 'planning',
    requiredPermissions: ['fleet.agreement.read'],
    configSchema: fleetAgreementExpiryConfig,
    /* Ninety days: long enough that a retender can still be started, short
       enough that the list is not everything. */
    defaultConfiguration: { withinDays: 90 },
    defaultSize: { width: 3, height: 3 },
    minSize: { width: 2, height: 2 },
    refresh: { policy: 'interval', intervalSeconds: 3600 },
    dataSource: 'fleet.expiring',
    configVersion: 1,
  },
  'fleet-energy-mix': {
    type: 'fleet-energy-mix',
    titleKey: 'components.fleetEnergyMix.title',
    descriptionKey: 'components.fleetEnergyMix.description',
    category: 'planning',
    requiredPermissions: ['fleet.read'],
    configSchema: fleetEnergyMixConfig,
    defaultConfiguration: { showAlternativeSplit: false },
    defaultSize: { width: 4, height: 3 },
    minSize: { width: 3, height: 2 },
    refresh: { policy: 'interval', intervalSeconds: 900 },
    dataSource: 'fleet.summary',
    configVersion: 1,
  },
  'fleet-verification': {
    type: 'fleet-verification',
    titleKey: 'components.fleetVerification.title',
    descriptionKey: 'components.fleetVerification.description',
    category: 'planning',
    requiredPermissions: ['fleet.read'],
    configSchema: fleetVerificationConfig,
    defaultConfiguration: {},
    defaultSize: { width: 3, height: 3 },
    minSize: { width: 2, height: 2 },
    refresh: { policy: 'interval', intervalSeconds: 900 },
    dataSource: 'fleet.summary',
    configVersion: 1,
  },
  'integration-health': {
    type: 'integration-health',
    titleKey: 'components.integrationHealth.title',
    descriptionKey: 'components.integrationHealth.description',
    category: 'monitoring',
    requiredPermissions: ['integration.read'],
    configSchema: integrationHealthConfig,
    defaultConfiguration: { onlyUnhealthy: true, limit: 5 },
    defaultSize: { width: 4, height: 3 },
    minSize: { width: 3, height: 2 },
    refresh: { policy: 'interval', intervalSeconds: 60 },
    dataSource: 'integrations.health',
    configVersion: 1,
  },
  'automation-activity': {
    type: 'automation-activity',
    titleKey: 'components.automationActivity.title',
    descriptionKey: 'components.automationActivity.description',
    category: 'monitoring',
    requiredPermissions: ['automation.read'],
    configSchema: automationActivityConfig,
    defaultConfiguration: { limit: 5, includeSkipped: true },
    defaultSize: { width: 4, height: 4 },
    minSize: { width: 3, height: 3 },
    refresh: { policy: 'interval', intervalSeconds: 60 },
    dataSource: 'automations.runs',
    configVersion: 1,
  },
  'discussion-activity': {
    type: 'discussion-activity',
    titleKey: 'components.discussionActivity.title',
    descriptionKey: 'components.discussionActivity.description',
    category: 'content',
    /**
     * `discussion.read` is the collaboration module's switch and nothing more.
     * Which conversations this reader may actually see is decided per item by
     * the target kind's own read permission, in the service -- a guard cannot
     * know what a comment is attached to.
     */
    requiredPermissions: ['discussion.read'],
    configSchema: discussionActivityConfig,
    defaultConfiguration: { limit: 5 },
    defaultSize: { width: 4, height: 4 },
    minSize: { width: 3, height: 3 },
    /* Conversation moves on a human timescale and the feed is a cross-kind
     * query; polling it every minute would be the most expensive tile on the
     * board for the least movement. */
    refresh: { policy: 'interval', intervalSeconds: 300 },
    dataSource: 'discussion.recent',
    configVersion: 1,
  },
  'markdown-note': {
    type: 'markdown-note',
    titleKey: 'components.markdownNote.title',
    descriptionKey: 'components.markdownNote.description',
    category: 'content',
    requiredPermissions: [],
    configSchema: markdownNoteConfig,
    defaultConfiguration: { body: '' },
    defaultSize: { width: 4, height: 3 },
    minSize: { width: 2, height: 2 },
    refresh: { policy: 'manual' },
    dataSource: null,
    configVersion: 1,
  },
} as const satisfies Record<string, ComponentDefinition>;

export type ComponentType = keyof typeof COMPONENT_CATALOG;
export const COMPONENT_TYPES = Object.keys(COMPONENT_CATALOG) as ComponentType[];

export function getComponentDefinition(type: string): ComponentDefinition | undefined {
  return (COMPONENT_CATALOG as Record<string, ComponentDefinition>)[type];
}

export function isComponentType(type: string): type is ComponentType {
  return Object.prototype.hasOwnProperty.call(COMPONENT_CATALOG, type);
}

/**
 * Catalog entry as delivered to the browser. Zod schemas cannot cross the wire,
 * so the API converts each `configSchema` to JSON Schema for form generation.
 */
export const catalogEntryWireSchema = z.object({
  type: z.string(),
  titleKey: z.string(),
  descriptionKey: z.string(),
  category: componentCategorySchema,
  requiredPermissions: z.array(z.string()),
  configJsonSchema: z.record(z.string(), z.unknown()),
  defaultConfiguration: z.record(z.string(), z.unknown()),
  defaultSize: gridSizeSchema,
  minSize: gridSizeSchema,
  refresh: refreshPolicySchema,
  dataSource: z.string().nullable(),
  configVersion: z.number().int(),
  /** False when the current user lacks the required permissions. */
  available: z.boolean(),
});
export type CatalogEntryWire = z.infer<typeof catalogEntryWireSchema>;
